<template>
  <div class="ocr-overlay" v-if="show" @click.self="close">
    <div class="ocr-box">
      <div class="ocr-head">
        <span class="ocr-title">Состав из картинки</span>
        <span class="ocr-team">{{ teamName }}</span>
      </div>

      <div class="ocr-body">
        <div v-if="loading" class="ocr-loading">Распознаём…</div>

        <div v-else-if="error" class="ocr-error">{{ error }}</div>

        <div v-else class="ocr-cols">
          <!-- Слева исходная картинка: глазами сверить проще, чем доверять распознаванию -->
          <div class="ocr-pane ocr-pane-shot">
            <div class="ocr-pane-cap">Что вставили</div>
            <div class="ocr-shot" @click="zoom = true">
              <img :src="image" alt="вставленный состав" @load="onImgLoad" />
              <!-- Рамка в процентах от картинки: при любом размере превью встаёт на место -->
              <div v-if="hoverBox" class="ocr-mark" :style="hoverBox"></div>
              <span class="ocr-zoom">увеличить</span>
            </div>
          </div>

          <div class="ocr-pane">
            <p class="ocr-hint">
              Разобрано {{ rows.length }} игроков. Сверь со скриншотом слева — строка лавки
              с несколькими фамилиями разбивается на отдельных игроков, ошибки распознавания
              видно ниже.
            </p>

            <div class="ocr-list">
              <div class="ocr-row ocr-row-head">
                <span>С картинки</span>
                <span>Игрок в составе</span>
                <span>Куда</span>
              </div>

              <div class="ocr-row" v-for="(row, i) in rows" :key="i"
                   @mouseenter="hover = i" @mouseleave="hover = -1">
                <span class="ocr-src">
                  <b v-if="row.number">{{ row.number }}</b> {{ row.name }}
                </span>
                <span class="ocr-match" :class="row.matched ? (row.byNumber ? 'guess' : 'ok') : 'bad'">
                  {{ row.matched ? row.matched.name : 'нет в составе' }}
                  <em v-if="row.byNumber">только по номеру</em>
                </span>
                <select v-model="row.lu" class="ocr-select" :disabled="!row.matched">
                  <option value="start">старт</option>
                  <option value="sub">лавка</option>
                  <option value="skip">пропустить</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Картинку в превью мелкая, поэтому по клику показываем целиком -->
      <div v-if="zoom" class="ocr-zoombox" @click="zoom = false">
        <img :src="image" alt="вставленный состав целиком" />
      </div>

      <div class="ocr-foot">
        <span class="ocr-count" v-if="!loading && !error">
          проставим: {{ readyCount }}
        </span>
        <!-- Заявка — чёрные точки: тот же список игроков, но не LU, а цветная метка -->
        <button type="button" class="ocr-btn-black" @click="applyBlack" :disabled="loading || readyCount === 0">
          Заявка
        </button>
        <!-- Картинка бывает только с лавкой: выбирать «лавка» в каждой строке руками долго -->
        <button type="button" class="ocr-btn-bench" @click="applyAll('sub')" :disabled="loading || readyCount === 0">
          Проставить лавку
        </button>
        <button type="button" class="ocr-btn-cancel" @click="close">Отмена</button>
        <button type="button" class="ocr-btn-ok" @click="apply" :disabled="loading || readyCount === 0">
          Проставить LU
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "lineupFromImage",
  props: {
    show: { type: Boolean, default: false },
    teamName: { type: String, default: "" },
    teamsId: { type: String, default: "" },
    squad: { type: Array, default: () => [] }
  },
  data: () => ({
    loading: false,
    error: "",
    rows: [],
    image: "",
    imgW: 0,
    imgH: 0,
    hover: -1,
    zoom: false
  }),
  computed: {
    readyCount() {
      return this.rows.filter(r => r.matched && r.lu !== 'skip').length;
    },

    // Рамку держим в процентах: превью можно тянуть как угодно, попадание не поедет
    hoverBox() {
      const row = this.rows[this.hover];
      if (!row || !row.box || !this.imgW || !this.imgH) return null;
      const b = row.box;
      // Пара пикселей запаса, иначе рамка режет буквы по краям
      const pad = 2;
      return {
        left: ((b.x - pad) / this.imgW * 100) + '%',
        top: ((b.y - pad) / this.imgH * 100) + '%',
        width: ((b.w + pad * 2) / this.imgW * 100) + '%',
        height: ((b.h + pad * 2) / this.imgH * 100) + '%'
      };
    }
  },
  methods: {
    onImgLoad(e) {
      this.imgW = e.target.naturalWidth;
      this.imgH = e.target.naturalHeight;
    },

    // Картинка приходит на языке лиги (кириллица у Казахстана и России, греческий
    // у Греции), а ростер на Transfermarkt всегда латиницей — без перевода в латиницу
    // не совпадает вообще ничего. Иероглифы и арабицу так не перевести: там нужен
    // словарь имён, а не таблица букв, — для них остаётся привязка по номеру.
    translit(s) {
      const map = {
        // русский, украинский, белорусский, казахский, сербский, болгарский
        'а':'a','б':'b','в':'v','г':'g','д':'d','е':'e','ё':'e','ж':'zh','з':'z',
        'и':'i','й':'i','к':'k','л':'l','м':'m','н':'n','о':'o','п':'p','р':'r',
        'с':'s','т':'t','у':'u','ф':'f','х':'h','ц':'ts','ч':'ch','ш':'sh',
        'щ':'sch','ъ':'','ы':'y','ь':'','э':'e','ю':'yu','я':'ya',
        'є':'e','і':'i','ї':'i','ґ':'g','ў':'u',
        'ә':'a','ғ':'g','қ':'k','ң':'n','ө':'o','ұ':'u','ү':'u','һ':'h',
        'ђ':'dj','ј':'j','љ':'lj','њ':'nj','ћ':'c','џ':'dz',
        // греческий
        'α':'a','β':'v','γ':'g','δ':'d','ε':'e','ζ':'z','η':'i','θ':'th',
        'ι':'i','κ':'k','λ':'l','μ':'m','ν':'n','ξ':'x','ο':'o','π':'p',
        'ρ':'r','σ':'s','ς':'s','τ':'t','υ':'i','φ':'f','χ':'ch','ψ':'ps','ω':'o'
      };

      return String(s || '')
        .toLowerCase()
        .split('')
        .map(ch => (ch in map ? map[ch] : ch))
        .join('');
    },

    // Одну и ту же фамилию каждый сайт пишет по-своему: Мичевич — Micevic или
    // Michevich, Кучер — Kucher или Kucer. Поэтому сравниваем не буквы, а огрублённый
    // скелет слова: одинаковые по звучанию сочетания сводим к одной букве.
    norm(s) {
      let v = this.translit(s)
        .normalize('NFD').replace(/[̀-ͯ]/g, '')   // диакритика: Kovač → Kovac
        .toLowerCase().replace(/[^a-z0-9]/g, '');

      v = v
        .replace(/sch|shch/g, 's')
        .replace(/sh|zh|ch|ts|kh|ph|th/g, m => ({ sh:'s', zh:'z', ch:'c', ts:'c', kh:'h', ph:'f', th:'t' })[m])
        .replace(/ck/g, 'k')
        .replace(/x/g, 'ks')
        .replace(/[wq]/g, m => (m === 'w' ? 'v' : 'k'))
        .replace(/[yj]/g, 'i')
        .replace(/h/g, '')
        .replace(/(.)+/g, '$1');   // двойные буквы: Mueller → Muler

      return v;
    },

    // Насколько два скелета похожи: 1 — одинаковые, 0 — ничего общего.
    // Нужно, потому что распознавание путает буквы, а транслитерация всё равно
    // не даёт точного совпадения (Стоисавлевич vs Stoisavljevic)
    similarity(a, b) {
      if (!a || !b) return 0;
      if (a === b) return 1;

      const rows = a.length + 1;
      const cols = b.length + 1;
      let prev = Array.from({ length: cols }, (_, i) => i);

      for (let i = 1; i < rows; i++) {
        const curr = [i];
        for (let j = 1; j < cols; j++) {
          curr[j] = Math.min(
            prev[j] + 1,
            curr[j - 1] + 1,
            prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
          );
        }
        prev = curr;
      }

      return 1 - prev[cols - 1] / Math.max(a.length, b.length);
    },

    /**
     * Слова имени по отдельности, включая половинки дефисной фамилии.
     *
     * Vision дефис не отдаёт: «Maynard-Brewer» приходит как «Maynard Brewer».
     * В ростере же это ОДНО слово, и половинка с ним никогда не совпадала —
     * Maynard-Brewer и Keillor-Dunn так и оставались без отметки.
     */
    nameParts(name) {
      return String(name).split(/[\s-]+/)
        .map(w => this.norm(w))
        .filter(w => w.length > 1);
    },

    // Насколько распознанный текст похож на имя игрока: берём лучшее из
    // сравнения с полным именем и с каждым словом (и половинкой дефисной фамилии)
    nameCloseness(text, player) {
      const t = this.norm(text);
      if (!t || !player) return 0;
      return Math.max(
        this.similarity(t, this.norm(player.name)),
        ...this.nameParts(player.name).map(p => this.similarity(t, p)),
      );
    },

    findPlayer(row) {
      const target = this.norm(row.name);
      if (!target) return null;

      const byName = this.squad.find(p => this.norm(p.name) === target);
      if (byName) return byName;

      // Фамилия отдельно: на картинках обычно только она, а в ростере полное имя
      const surname = this.norm(row.name.split(/\s+/).pop());
      const bySurname = this.squad.filter(p => surname.length > 2
        && (this.norm(p.name).endsWith(surname) || this.nameParts(p.name).includes(surname)));
      if (bySurname.length === 1) return bySurname[0];

      // Однофамильцы — разводим по номеру
      if (bySurname.length > 1 && row.number) {
        const byNumber = bySurname.find(p => String(p.shirtnumber) === String(row.number));
        if (byNumber) return byNumber;
      }

      // Номера нет — разводим по инициалу («J.HUNT» это Jack Hunt, а не Alex Hunt).
      // Без этого строка пропадала совсем: у обоих однофамильцев сходство с «HUNT»
      // ровно 1.00, фаззи требует отрыва от второго и отдаёт null
      if (bySurname.length > 1 && row.initial) {
        const letter = String(row.initial).toLowerCase();
        const byInitial = bySurname.filter(p => String(p.name).trim().toLowerCase().startsWith(letter));
        if (byInitial.length === 1) return byInitial[0];
      }

      // Ничего не совпало буква в букву — ищем самого похожего. Порог высокий и
      // лучший должен заметно опережать второго, иначе легко приписать не того
      const scored = this.squad
        .map((p) => {
          // Сравниваем и с полным именем, и с каждым словом по отдельности:
          // на картинке обычно одно слово, а в ростере «Имя Фамилия», причём
          // на снимке может стоять как фамилия, так и имя
          const full = this.norm(p.name);
          const words = this.nameParts(p.name).filter(w => w.length > 2);

          const score = Math.max(
            this.similarity(target, full),
            ...words.map(w => this.similarity(target, w))
          );

          return { player: p, score };
        })
        .sort((a, b) => b.score - a.score);

      if (scored.length && scored[0].score >= 0.8 &&
          (scored.length === 1 || scored[0].score - scored[1].score >= 0.08)) {
        return scored[0].player;
      }

      return null;
    },

    /**
     * Последняя надежда: игрок с таким номером в ростере.
     *
     * Шаг ненадёжный — у «22. Anthony Lens» в ростере под 22-м стоит совсем другой
     * человек, и раньше он молча уезжал в состав. Поэтому вынесен отдельно: такие
     * строки помечаются и по умолчанию пропускаются.
     */
    findByNumber(number) {
      if (!number) return null;
      return this.squad.find(p => String(p.shirtnumber) === String(number)) || null;
    },

    // Лавку на картинках печатают одной строкой и часто без запятых, поэтому в
    // одну распознанную ячейку попадает 2-3 фамилии сразу. Разбираем ячейку на
    // слова и набираем игроков жадно: сначала пробуем тройку слов, потом пару,
    // потом одно слово. Так «Fabio Blaga» остаётся одним игроком, а
    // «Лехаир Ковачи Занц» превращается в трёх.
    splitRow(row, ctx) {
      const used = ctx.used;
      const keyOf = (p) => `${p.name}|${p.shirtnumber}`;

      // Запятая, точка с запятой и двоеточие — жёсткая граница между игроками:
      // «Rezerve: Iacob, Dohatcă, Dumitru» это подпись и три фамилии, а не одно имя
      const chunks = String(row.name || '')
        .replace(/\([^)]*\)/g, ' ')            // пометки вроде (GK)
        .split(/[,;:]+/)
        .map(c => c.trim())
        .filter(Boolean);

      const out = [];
      let number = row.number || null;

      chunks.forEach((chunk) => {
        const tokens = chunk.split(/[\s.]+/).map(t => t.trim()).filter(Boolean);
        let i = 0;
        let initial = null;

        while (i < tokens.length) {
          // Отдельно стоящее число — это номер следующего игрока, а не его имя
          if (/^\d{1,3}$/.test(tokens[i])) {
            number = tokens[i];
            i++;
            continue;
          }

          // Одинокая буква — это инициал имени: придержим её для следующей фамилии
          if (!this.isLabel(tokens[i]) && /^[A-Za-zА-Яа-я]$/.test(tokens[i])) {
            initial = tokens[i];
            i++;
            continue;
          }

          // Подпись строки и одинокие инициалы игроками не считаем.
          // Заодно "SUBSTITUTES" переключает всё, что ниже, на лавку: считать
          // первых 11 совпавших стартом нельзя — игроков с картинки может не
          // оказаться в ростере, и счётчик добирает недостачу с лавки.
          if (this.isLabel(tokens[i]) || this.norm(tokens[i]).length < 2) {
            const kind = this.labelKind(tokens[i]);
            if (kind) {
              ctx.bench = kind === 'bench';
              // Рамка подписи нужна, чтобы разделить старт и лавку по месту на
              // картинке, а не по порядку чтения: на сетке карточек «SUBS» стоит
              // СПРАВА от нижнего ряда стартовых, и порядок чтения врёт
              ctx.labelBox = kind === 'bench' ? (row.box || null) : null;
            }
            i++;
            continue;
          }

          let player = null;
          let taken = 1;
          let text = tokens[i];

          for (let size = Math.min(3, tokens.length - i); size >= 1; size--) {
            const window = tokens.slice(i, i + size).filter(w => !/^\d+$/.test(w));
            if (window.length !== size) continue;

            const candidate = this.findPlayer({ name: window.join(' '), number, initial });
            if (candidate && !used.has(keyOf(candidate)) && this.windowFits(window, candidate)) {
              player = candidate;
              taken = size;
              text = window.join(' ');
              break;
            }
          }

          // По имени не вышло — пробуем номер, но помечаем такую находку
          let byNumber = false;
          if (!player && number) {
            const guess = this.findByNumber(number);
            if (guess && !used.has(keyOf(guess))) {
              player = guess;
              // Номер сошёлся — смотрим, похоже ли имя хоть немного. Покалеченный
              // распознаванием текст даёт около половины сходства («Maynard» против
              // «Maynard-Brewer» — 0.54), и это тот же человек: ставим метку.
              // А мусор из шапки картинки («TUES 22 SEP» с номером 22) на игрока не
              // похож вовсе — он остаётся пометкой «только по номеру» и, главное,
              // не занимает игрока: иначе настоящая строка с ним уже не сойдётся.
              byNumber = this.nameCloseness(text, guess) < 0.4;
            }
          }

          if (player && !byNumber) used.add(keyOf(player));

          // box один на всю ячейку: подсветится она целиком, а не отдельное слово
          out.push({ ...row, name: text, number, matched: player, byNumber, bench: ctx.bench });

          i += taken;
          number = null;   // номер относится только к одному игроку
          initial = null;  // и инициал тоже
        }
      });

      if (out.length === 0) out.push({ ...row, matched: null, bench: ctx.bench });
      return out;
    },

    // Подписи строк состава — на картинке они стоят вплотную к первой фамилии
    labelKind(word) {
      const bench = ['rezerve', 'rezerva', 'reserve', 'reserves', 'sub', 'subs',
                     'substitutes', 'substitutions', 'bench', 'zapasnye', 'zameny', 'lavka'];
      const start = ['sostav', 'start', 'startingxi', 'starting', 'xi', 'lineup'];
      // Список гоняем через ту же norm(): она съедает 'h' и меняет x на ks,
      // поэтому сырые 'bench' и 'xi' не совпали бы со своими же скелетами
      const w = this.norm(word);
      if (bench.some(l => this.norm(l) === w)) return 'bench';
      if (start.some(l => this.norm(l) === w)) return 'start';
      return null;
    },

    isLabel(word) {
      return this.labelKind(word) !== null;
    },

    // Взяли несколько слов как одно имя — проверяем, что КАЖДОЕ из них есть в имени
    // игрока. Иначе «Rezerve Iacob Dohatcă» цеплялось за Dohatcă и съедало соседей
    windowFits(window, player) {
      if (window.length < 2) return true;

      const parts = this.nameParts(player.name);
      const fits = (t) => parts.some(p => p === t || this.similarity(p, t) >= 0.7);

      // Фамилия (последнее слово) обязана сойтись — на ней всё и держится
      const last = this.norm(window[window.length - 1]);
      if (last.length >= 2 && !fits(last)) return false;

      let misses = 0;
      for (const word of window.slice(0, -1)) {
        const t = this.norm(word);
        if (t.length < 2) continue;   // инициал
        if (fits(t)) continue;

        // Слово, которое само точно совпадает с фамилией ДРУГОГО игрока ростера, —
        // это сосед по списку, а не второе имя: «Tyrer Turner» склеивать нельзя
        if (this.squad.some(p => p !== player && this.norm(p.name).endsWith(t))) return false;

        misses++;
      }

      // Имя на картинке нередко своё: у Гальвиса написано «Jhoel», в ростере «Hector».
      // Раньше такое расхождение отменяло пару целиком, и строка разваливалась на двух
      // «игроков» — фамилия уходила своему, а имя фаззи-матчем цеплялось к чужому
      return misses <= 1;
    },

    async recognize(dataUrl) {
      this.loading = true;
      this.error = "";
      this.rows = [];
      this.image = dataUrl;   // ту же картинку показываем слева — лишнего запроса нет
      this.hover = -1;
      this.zoom = false;
      try {
        const { data } = await this.$axios.post('/ocr/lineup', { image: dataUrl }, {
          headers: { Authorization: this.$cookie ? this.$cookie.get('token') : '' }
        });

        const found = (data && data.rows) || [];

        // Одного игрока дважды не проставляем: он мог попасться и в старте, и в лавке
        const ctx = { used: new Set(), bench: false, labelBox: null };
        const split = this.applyBenchGeometry(
          this.mergeSplitNames(
            found.reduce((acc, r) => acc.concat(this.splitRow(r, ctx)), [])
          ),
          ctx.labelBox
        );

        // Ниже подписи "SUBSTITUTES" — только лавка. Где подписи нет, работает
        // старое правило: первые 11 совпавших — старт, дальше правится руками.
        // Считаем НАЙДЕННЫХ ИГРОКОВ, а не строки картинки. По строкам считали из-за
        // развалившихся надвое фамилий, но их теперь склеивает mergeSplitNames, зато
        // лавку почти всегда печатают списком — несколько фамилий в одной строке.
        // Такая строка считалась одной, и запасные уезжали в старт: на прогоне 68
        // английских карточек счёт по строкам давал ровно 11 у 40 штук, по игрокам —
        // у 48 (Crawley 14→11, Stoke 17→11, Fleetwood 14→11), и ни одной не сломал.
        let matched = 0;

        this.rows = split.map(r => {
          // Запасных в счёт старта не берём: у York City блок «SUBS» стоит выше
          // нижнего ряда карточек, Farrar занимал одиннадцатое место, и McArthur
          // уезжал в лавку
          if (r.matched && !r.byNumber && !r.bench) matched++;
          const lu = r.bench ? 'sub' : (matched <= 11 ? 'start' : 'sub');
          // Найденных только по номеру не проставляем молча: имя-то не сошлось
          return { ...r, lu: (!r.matched || r.byNumber) ? 'skip' : lu };
        });

        if (this.rows.length === 0) this.error = "На картинке не нашлось текста.";
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.error) || e.message;
        this.error = `Не получилось: ${msg}`;
      } finally {
        this.loading = false;
      }
    },


    /**
     * Кто из строк относится к лавке, если у подписи есть рамка.
     *
     * Порядок чтения от Vision не годится: у Bournemouth и Leyton Orient заголовок
     * «SUBS» стоит правее нижнего ряда стартовых карточек и приходит раньше них,
     * из-за чего трое стартовиков уезжали в запас. Поэтому смотрим на место:
     *  — выше подписи   — всегда старт;
     *  — на одной высоте — лавка только правее подписи;
     *  — ниже подписи   — лавка, если попадает в её колонку (или правее).
     */
    applyBenchGeometry(rows, labelBox) {
      if (!labelBox) return rows;

      const labelTop = labelBox.y;
      const labelBottom = labelBox.y + labelBox.h;
      const labelLeft = labelBox.x;
      const labelRight = labelBox.x + labelBox.w;

      return rows.map((r) => {
        const b = r.box;
        if (!b) return r;   // без рамки остаётся прежняя пометка по порядку чтения

        const top = b.y;
        const bottom = b.y + b.h;
        const left = b.x;
        const right = b.x + b.w;

        if (bottom <= labelTop) return { ...r, bench: false };

        const sameBand = top < labelBottom && bottom > labelTop;
        if (sameBand) return { ...r, bench: left >= labelLeft };

        const inColumn = right > labelLeft && left < labelRight;
        return { ...r, bench: inColumn || left >= labelLeft };
      });
    },

    // OCR рвёт двойные фамилии на две строки: «52 RAMIREZ» и «ESPAIN». Вторая
    // половина игрока находит, первая остаётся сиротой и лезла в плашку
    // «нет в составе». Склеиваем такую пару обратно, номер берём у первой.
    mergeSplitNames(rows) {
      const out = [];

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const next = rows[i + 1];
        const part = this.norm(row.name);

        if (!row.matched && next && next.matched && part.length >= 3 &&
            this.norm(next.matched.name).indexOf(part) !== -1) {
          out.push({
            ...next,
            name: `${row.name}-${next.name}`,
            number: row.number || next.number
          });
          i++;   // вторую половину отдельной строкой не показываем
          continue;
        }

        out.push(row);
      }

      return out;
    },

    // Игроки старта, которых нет в ростере (молодёжь, новички) — их надо показать
    // на главном экране: иначе о них узнаёшь, только пересчитав старт вручную.
    missedStart(includeBench = false) {
      const numbered = this.rows.some(r => r.number);
      return this.rows
        // У заявки деления на старт и лавку нет — там показываем всех ненайденных
        .filter(r => !r.matched && (includeBench || !r.bench))
        // Если на картинке есть номера, берём только строки с номером: так
        // отсеиваются подписи и логотипы («WATFORD», «STARTING XI», «BOXT»)
        .filter(r => (numbered ? !!r.number : this.norm(r.name).length > 2))
        // Обрывки заголовков вроде «18 ST» от «STARTING XI»: номер есть, имени нет
        .filter(r => this.norm(r.name).length >= 3 && !this.isLabel(r.name))
        .map(r => (r.number ? `${r.number} ${r.name}` : r.name));
    },

    apply() {
      this.$emit('missed', this.missedStart());
      const payload = this.rows
        .filter(r => r.matched && r.lu !== 'skip')
        .map(r => ({ name: r.matched.name, shirtnumber: r.matched.shirtnumber, lu: r.lu }));

      this.$emit('apply', payload);
      this.close();
    },

    // Всех найденных — одним статусом, что бы ни стояло в выпадающих списках
    applyAll(lu) {
      // Картинка целиком про лавку: там стоит «SUBSTITUTES», из-за чего все строки
      // помечены лавкой, и список ненайденных по старт-секции выходил пустым
      this.$emit('missed', this.missedStart(true));
      const payload = this.rows
        .filter(r => r.matched && r.lu !== 'skip')
        .map(r => ({ name: r.matched.name, shirtnumber: r.matched.shirtnumber, lu }));

      this.$emit('apply', payload);
      this.close();
    },

    // Заявка — не LU, а чёрная точка в отдельной колонке
    applyBlack() {
      this.$emit('missed', this.missedStart(true));
      const payload = this.rows
        .filter(r => r.matched && r.lu !== 'skip')
        .map(r => ({ name: r.matched.name, shirtnumber: r.matched.shirtnumber }));

      this.$emit('apply-black', payload);
      this.close();
    },

    close() {
      this.rows = [];
      this.error = "";
      this.image = "";
      this.hover = -1;
      this.zoom = false;
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
.ocr-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex; align-items: center; justify-content: flex-start;
  z-index: 100000;
  padding: 12px 20px 12px 174px;
}
.ocr-box {
  width: 100%; max-width: 1400px;
  background: #fff; border-radius: 12px; overflow: hidden;
  text-align: left;
  box-shadow: 0 8px 24px rgba(0,0,0,0.2);
}
.ocr-head {
  background: #eaf3de; padding: 12px 18px;
  display: flex; align-items: center; justify-content: space-between;
}
.ocr-title { font-size: 16px; font-weight: 500; color: #173404; }
.ocr-team { font-size: 13px; color: #3b6d11; }
.ocr-body { padding: 14px 18px; max-height: calc(100vh - 150px); overflow-y: auto; }
.ocr-cols { display: grid; grid-template-columns: 672px minmax(0, 1fr); gap: 20px; }
.ocr-pane { min-width: 0; }
/* Картинка остаётся на виду, пока крутишь список справа */
.ocr-pane-shot { position: sticky; top: 0; align-self: start; }
.ocr-pane-cap { font-size: 12px; color: #6c757d; margin-bottom: 6px; }
.ocr-shot {
  position: relative; border: 1px solid #eee; border-radius: 8px;
  overflow: hidden; cursor: zoom-in; background: #fafafa;
}
.ocr-shot img { display: block; width: 100%; }
/* Рамка поверх картинки — только подсказка, кликам не мешает */
.ocr-mark {
  position: absolute; pointer-events: none;
  border: 2px solid #e24b4a; border-radius: 2px;
  background: rgba(226, 75, 74, 0.14);
}
.ocr-zoom {
  position: absolute; right: 6px; bottom: 6px;
  background: rgba(0,0,0,0.6); color: #fff;
  font-size: 11px; padding: 3px 8px; border-radius: 4px;
}
.ocr-zoombox {
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.85); z-index: 100001;
  display: flex; align-items: center; justify-content: center;
  padding: 20px; cursor: zoom-out; overflow: auto;
}
.ocr-zoombox img { max-width: 100%; }
/* На узком экране колонки складываются: картинка сверху, список под ней */
@media (max-width: 1250px) {
  .ocr-cols { grid-template-columns: minmax(0, 1fr); }
  .ocr-pane-shot { position: static; }
}
.ocr-hint { margin: 0 0 10px; font-size: 13px; color: #6c757d; }
.ocr-loading, .ocr-error { padding: 20px 0; text-align: center; font-size: 14px; }
.ocr-error { color: #b72929; }
.ocr-list { border: 1px solid #eee; border-radius: 8px; overflow: hidden; }
.ocr-row {
  display: grid; grid-template-columns: 1fr 1fr 110px;
  gap: 8px; align-items: center;
  padding: 7px 10px; border-top: 1px solid #eee; font-size: 14px;
}
.ocr-row:first-child { border-top: none; }
.ocr-row:hover:not(.ocr-row-head) { background: #f4f8ee; }
.ocr-row-head { background: #f7f7f7; font-size: 12px; color: #6c757d; }
.ocr-src b { margin-right: 4px; }
.ocr-match.ok { color: #3b6d11; }
.ocr-match.bad { color: #a32d2d; }

/* Привязка только по номеру: имя не сошлось, поэтому строка не зелёная и по
   умолчанию стоит «пропустить» — глазами видно, что решение спорное */
.ocr-match.guess { color: #b26a00; }
.ocr-match em {
  display: block;
  font-size: 11px;
  font-style: normal;
  color: #b26a00;
  opacity: .85;
}
.ocr-select { height: 26px; font-size: 13px; }
.ocr-foot {
  padding: 12px 18px 16px;
  display: flex; align-items: center; justify-content: flex-end; gap: 10px;
}
.ocr-count { margin-right: auto; font-size: 13px; color: #6c757d; }
.ocr-btn-cancel {
  background: transparent; border: 1px solid #ccc; border-radius: 8px;
  padding: 7px 16px; font-size: 14px; cursor: pointer;
}
/* Старт — красный, как кружок старта в таблице */
.ocr-btn-ok {
  background: #c0392b; color: #fff; border: none; border-radius: 8px;
  padding: 18px 48px; font-size: 20px; font-weight: 500; cursor: pointer;
}
/* Лавка — своим цветом, чтобы не путать с основной кнопкой */
.ocr-btn-bench {
  background: #c8960c; color: #fff; border: none; border-radius: 8px;
  padding: 8px 18px; font-size: 14px; cursor: pointer;
}
/* Заявка — чёрная, как сама точка в таблице */
.ocr-btn-black {
  background: #1a1a1a; color: #fff; border: none; border-radius: 8px;
  padding: 8px 18px; font-size: 14px; cursor: pointer;
}
.ocr-btn-black:disabled { background: #9a9a9a; cursor: default; }
.ocr-btn-bench:disabled { background: #ddc98d; cursor: default; }
.ocr-btn-ok:disabled { background: #dcaaa4; cursor: default; }
</style>
